import {
  fetchBaseQuery,
  createApi,
  BaseQueryFn,
  FetchArgs
} from '@reduxjs/toolkit/query/react'
import { FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query'
import { RootState } from '../app/store'
import {
  CheckinEntriesResponse,
  UpdateEntriesPayload,
  UpdateEntriesResponse,
  ValidateCodePayload,
  ValidateCodeResponse
} from '../app/types'
import { cacheToken, clearSession } from '../features/session/sessionSlice'
import { addUnsyncedRequests } from '../features/data/dataSlice'
import { createLogger } from '../app/logger'
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes'

const logger = createLogger({
  prefix: 'ctliveApi'
})

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_CTLIVE_API_URL || 'http://localhost:3000',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).session.token
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    } else {
      logger.warn('No token available for API call')
    }

    const accessCode = (getState() as RootState).session.accessCode

    if (accessCode) {
      headers.set('x-access-code', accessCode)
    } else {
      logger.warn('No access code available for API call')
    }

    return headers
  }
})

const ACCESS_CODE_ERRORS = [
  'invalid access code',
  'check-in code not provided',
] as const

const isAccessCodeError = (errorData: unknown): boolean => {
  if (
    errorData &&
    typeof errorData === 'object' &&
    'error' in errorData
  ) {
    const errorMessage = (errorData as { error: string }).error
    return ACCESS_CODE_ERRORS.some(
      knownError => errorMessage.toLowerCase() === knownError.toLowerCase()
    )
  }
  return false
}

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions)
  if (result.error && result.error.status === 401) {
    // Clear session if 401 is due to access code issues
    // (invalid/revoked code, or code not provided due to stale session)
    if (isAccessCodeError(result.error.data)) {
      logger.warn('Access code error, clearing session', result.error.data)
      api.dispatch(clearSession())
      return result
    }

    logger.warn('Unauthorized API call, attempting reauthentication...', args)
    const refreshResult = await baseQuery({
      url: 'registration/createAnonymousSession',
      method: 'POST',
      body: {
        clientId: 'checkin'
      }
    }, api, {}) as QueryReturnValue<{ token: string }, FetchBaseQueryError, FetchBaseQueryMeta>

    if (refreshResult.data && refreshResult.data.token) {
      // store the new token
      api.dispatch(cacheToken(refreshResult.data.token))
      // retry the initial query
      result = await baseQuery(args, api, extraOptions)
    } else {
      logger.warn('Could not reauthorize and make API call: ', { args, refreshResult })
    }
  }
  return result
}

const baseQueryWithNetworkCheck: BaseQueryFn<string | FetchArgs,
  unknown,
  FetchBaseQueryError> = async (args, api, extraOptions) => {
    const state = api.getState() as RootState
    const { network } = state
    const { online } = network
    const fetchArgs = args as FetchArgs
    const isUpdateEntries = fetchArgs.method === 'PUT' && fetchArgs.url?.indexOf('/checkin-entries')
    logger.info('baseQueryWithNetworkCheck: ', {
      args,
      api,
      network,
      extraOptions
    })

    if (!online) {
      logger.warn('baseQueryWithNetworkCheck NOT ONLINE: ', {
        args,
        api,
        network,
        extraOptions
      })
      if (isUpdateEntries) {
        api.dispatch(addUnsyncedRequests(fetchArgs.body))
      }
      return {
        data: []
      }
    }

    return await baseQueryWithReauth(args, api, extraOptions)
  }

const ctliveApi = createApi({
  reducerPath: 'ctliveApi',
  baseQuery: baseQueryWithNetworkCheck,
  endpoints: build => {
    return ({
      createAnonymousSession: build.mutation<{ token: string }, void>({
        query: () => ({
          url: 'registration/createAnonymousSession',
          method: 'POST',
          body: {
            clientId: 'checkin'
          }
        })
      }),
      getCheckinEntries: build.query<CheckinEntriesResponse, number>({
        query: (eventId: number) => ({
          url: `/pickup/event/${eventId}/entries`,
          method: 'GET'
        })
      }),
      updateEntries: build.mutation<UpdateEntriesResponse, UpdateEntriesPayload>({
        query: ({
          entries,
          eventId,
          kioskId,
          username,
        }: UpdateEntriesPayload) => ({
          url: `/pickup/event/${eventId}/entries`,
          method: 'PUT',
          body: {
            entries,
            kioskId,
            username,
          }
        })
      }),
      postValidateCode: build.mutation<ValidateCodeResponse, ValidateCodePayload>({
        query: ({
          eventId,
          code,
        }) => ({
          url: `/pickup/event/${eventId}/validate-code`,
          method: 'POST',
          body: {
            code,
          },
        })
      }),
    })
  }
})

export const {
  useCreateAnonymousSessionMutation,
  useGetCheckinEntriesQuery,
  useUpdateEntriesMutation,
  usePostValidateCodeMutation
} = ctliveApi

export default ctliveApi
